import { DomainEntity, Id, Json } from '@homeleite/arch';
import { MatingType } from './mating-type';
import { EventType } from '../event-type';
import { AnimalEvent } from '../animal-event';
import { AnimalSummary } from '../../animal';
import { BreederSummary } from '../../../breeders/breeder';
import { ProducerSummary } from '../../../producers/producer';
import { AuditData } from '../../auditData';

export abstract class Mating implements DomainEntity, AnimalEvent {

    protected constructor(
        public readonly id = Id.gen(),
        public animal: AnimalSummary = new AnimalSummary(),
        public date: Date,
        public father: BreederSummary = new BreederSummary(),
        public producer: ProducerSummary = new ProducerSummary(),
        public type: string = EventType.MATING.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {}

    abstract toJson();

    static fromJson(json: any): Mating {
        if (json?.type == null) {
            return null;
        }

        switch (json.type) {
            case MatingType.NATURAL.name:
                return NaturalMating.fromJson(json);
            case MatingType.ARTIFICIAL.name:
                return ArtificialMating.fromJson(json);
            case MatingType.IMPLANTATION.name:
                return ImplantationMating.fromJson(json);
            default:
                throw Error(`Mating [ type: ${json.type} ] not mapped`);
        }
    }
}

export class MatingSummary implements DomainEntity {

    constructor(
        public readonly id = Id.gen(),
        public animal: AnimalSummary = new AnimalSummary(),
        public father: BreederSummary = new BreederSummary(),
        public date: Date = null
    ) {}

    toJson() {
        return Json.to.simple(this, {
            animal: this.animal?.toJson() ?? null,
            father: this.father?.toJson() ?? null,
            date: this.date?.toISOString?.() ?? this.date ?? null
        })
    };

    static fromJson(json: any): MatingSummary {
        return json ? Json.from.simple(json ?? null, MatingSummary, {
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : new AnimalSummary(),
            father: json.father ? BreederSummary.fromJson(json.father) : new BreederSummary(),
            date: json.date ? new Date(json.date) : null
        })
        : null;
    };
}

export class ArtificialMating extends Mating {

    constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public father: BreederSummary = new BreederSummary(),
        public producer: ProducerSummary = new ProducerSummary(),
        public type: string = MatingType.ARTIFICIAL.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {
        super(id, animal, date, father, producer, type, created, auditData);
    }

    toJson() {
        return Json.to.simple(this, {
            date: this.date?.toISOString() ?? null,
            animal: this.animal?.toJson() ?? null,
            father: this.father?.toJson() ?? null,
            producer: this.producer?.toJson() ?? null,
            type: MatingType.ARTIFICIAL.name
        });
    }

    static fromJson(json: any): Mating {
        return json ? Json.from.simple(json, ArtificialMating, {
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : null,
            producer: json.producer ? ProducerSummary.fromJson(json.producer) : new ProducerSummary(),
            father: json.father ? BreederSummary.fromJson(json.father) : null,
            date: json.date ? new Date(json.date) : new Date(),
            created: json.created ? new Date(json.created) : null
        }) : null;
    }

    static createBy(natural: ArtificialMating, id: string): ArtificialMating {
        return new ArtificialMating(
            id,
            natural.date,
            natural.animal,
            natural.father,
            natural.producer,
        );
    }
}

export class NaturalMating extends Mating {

    constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public father: BreederSummary = new BreederSummary(),
        public producer: ProducerSummary = new ProducerSummary(),
        public type: string = MatingType.NATURAL.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {
        super(id, animal, date, father, producer, type, created, auditData);
    }

    toJson() {
        return Json.to.simple(this, {
            date: this.date?.toISOString() ?? null,
            animal: this.animal?.toJson() ?? null,
            father: this.father?.toJson() ?? null,
            producer: this.producer?.toJson() ?? null,
            type: MatingType.NATURAL.name
        });
    }

    static fromJson(json: any): Mating {
        return json ? Json.from.simple(json, NaturalMating, {
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : null,
            producer: json.producer ? ProducerSummary.fromJson(json.producer) : new ProducerSummary(),
            father: json.father ? BreederSummary.fromJson(json.father) : null,
            date: json.date ? new Date(json.date) : new Date(),
            created: json.created ? new Date(json.created) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null
        }) : null;
    }

    static createBy(natural: NaturalMating, id: string): NaturalMating {
        return new NaturalMating(
            id,
            natural.date,
            natural.animal,
            natural.father,
            natural.producer,
        );
    }
}

export class ImplantationMating extends Mating {
    constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public father: BreederSummary = new BreederSummary(),
        public producer: ProducerSummary = new ProducerSummary(),
        public embryDonor: AnimalSummary = null,
        public type: string = MatingType.IMPLANTATION.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {
        super(id, animal, date, father, producer, type, created, auditData);
    }

    toJson() {
        return Json.to.simple(this, {
            date: this.date?.toISOString() ?? null,
            animal: this.animal?.toJson() ?? null,
            father: this.father?.toJson() ?? null,
            producer: this.producer?.toJson() ?? null,
            embryDonor: this.embryDonor?.toJson() ?? null,
            type: MatingType.IMPLANTATION.name
        });
    }

    static fromJson(json: any): Mating {
        return json ? Json.from.simple(json, NaturalMating, {
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : null,
            producer: json.producer ? ProducerSummary.fromJson(json.producer) : new ProducerSummary(),
            father: json.father ? BreederSummary.fromJson(json.father) : null,
            date: json.date ? new Date(json.date) : new Date(),
            embryDonor: json.embryDonor ? AnimalSummary.fromJson(json.embryDonor) : null,
            created: json.created ? new Date(json.created) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null
        }) : null;
    }
}
